import { useState } from 'react';

/**
 * Hook personalizado para gestionar la API de facturación
 * Maneja las solicitudes al endpoint de facturas y los estados asociados
 */
const useInvoiceApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // URL base de la API
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Función para enviar la solicitud de factura
  const createInvoice = async (invoiceData) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoice: invoiceData }),
      });

      const data = await response.json();

      if (response.status === 201) {
        setSuccess({
          message: 'Factura enviada por correo, lista para descargar en PDF',
          data
        });
        return { success: true, data };
      } else {
        setError(data.error || 'Error al procesar la factura');
        return { success: false, error: data.error || 'Error al procesar la factura' };
      }
    } catch (err) {
      const errorMessage = 'Error de conexión al servidor';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  // Función para reiniciar los estados
  const resetStates = () => {
    setError(null);
    setSuccess(null);
  };

  // Función para verificar un código de servicio
  const verifyServiceCode = async (serviceCode) => {
    setIsLoading(true);
    setError(null);

    // Definir servicios de demostración
    const demoServices = {
      'SERV-12345': {
        descripcion: 'Consulta cardiológica general',
        valor_unitario: 800.00
      },
      'SERV-67890': {
        descripcion: 'Electrocardiograma de 12 derivaciones',
        valor_unitario: 1200.00
      },
      'ECOCAR-123': {
        descripcion: 'Ecocardiograma transtorácico',
        valor_unitario: 2500.00
      },
      'HOLTER-456': {
        descripcion: 'Monitoreo Holter de 24 horas',
        valor_unitario: 1800.00
      }
    };

    // Si es un código de demostración, devolver directamente
    if (demoServices[serviceCode]) {
      setIsLoading(false);
      return {
        valid: true,
        service: demoServices[serviceCode]
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/services/${serviceCode}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          valid: true,
          service: data.service
        };
      } else {
        // En entorno de desarrollo, permitir algunos códigos
        if (process.env.NODE_ENV === 'development' && Object.keys(demoServices).includes(serviceCode)) {
          return {
            valid: true,
            service: demoServices[serviceCode]
          };
        }

        return {
          valid: false,
          error: data.error || 'Código de servicio inválido'
        };
      }
    } catch (err) {
      console.error("Error de conexión:", err);

      // En entorno de desarrollo, permitir algunos códigos incluso cuando hay errores
      if (process.env.NODE_ENV === 'development' && Object.keys(demoServices).includes(serviceCode)) {
        return {
          valid: true,
          service: demoServices[serviceCode]
        };
      }

      setError('Error de conexión al verificar el código');
      return { valid: false, error: 'Error de conexión' };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    success,
    createInvoice,
    verifyServiceCode,
    resetStates
  };
};

export default useInvoiceApi;
